import {
  useMediaQuery,
  WhmBox,
  WhmButton,
  WhmDialog,
  WhmDialogActions,
  WhmDialogContent,
  WhmDialogTitle,
  WhmIconButton,
  WhmList,
  WhmListItem,
  WhmListSubheader,
  WhmStack,
  whmTheme,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { WhmResponsiveStack } from "../../../shared/WhmResponsiveStack";
import { ReactComponent as PassportOk } from "./PassportOk.svg";
import { ReactComponent as PassportBad1 } from "./PassportBad1.svg";
import { ReactComponent as PassportBad2 } from "./PassportBad2.svg";
import { ReactComponent as OkCheckmark } from "@securitize/assets-sec-ui/dist/icons/svg/success-filled.svg";
import { ReactComponent as BadCheckmark } from "@securitize/assets-sec-ui/dist/icons/svg/error-filled.svg";
import { ReactComponent as CloseIcon } from "@securitize/assets-sec-ui/dist/icons/svg/exit.svg";
import { translationKeys } from "../../../../contexts/I18nContext";
import { Translate } from "../../../Translation/Translate";

export default function PassportViewSampleDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));

  return (
    <WhmDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreen={isMobile}
      fullWidth
    >
      <WhmDialogTitle variant="h4">
        <Translate
          translationKey={
            translationKeys.PROFILE_KEY_PARTIES_PASSPORT_SAMPLE_UPLOADS
          }
        />
      </WhmDialogTitle>
      <WhmIconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: whmTheme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </WhmIconButton>
      <WhmDialogContent>
        <WhmStack gap="12px">
          <WhmResponsiveStack gap="16px" width="100%">
            <WhmStack flex="1">
              <WhmStack gap="16px" direction="row" justifyContent="center">
                <PassportOk />
              </WhmStack>
              <WhmList>
                <WhmListSubheader>
                  <Translate
                    translationKey={
                      translationKeys.PROFILE_KEY_PARTIES_SAMPLE_GOOD_UPLOAD
                    }
                  />
                </WhmListSubheader>
                <WhmListItem>
                  <WhmStack gap="8px" direction="row" alignItems="center">
                    <WhmBox flexShrink={0}>
                      <OkCheckmark
                        height="14px"
                        width="14px"
                        fill={whmTheme.palette.success.main}
                      />
                    </WhmBox>
                    <WhmTypography variant="body2">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_SAMPLE_GOOD_UPLOAD_FORMATS
                        }
                      />
                    </WhmTypography>
                  </WhmStack>
                </WhmListItem>
                <WhmListItem>
                  <WhmStack gap="8px" direction="row" alignItems="center">
                    <WhmBox flexShrink={0}>
                      <OkCheckmark
                        height="14px"
                        width="14px"
                        fill={whmTheme.palette.success.main}
                      />
                    </WhmBox>
                    <WhmTypography variant="body2">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_SAMPLE_GOOD_UPLOAD_PASSPORT_READABLE
                        }
                      />
                    </WhmTypography>
                  </WhmStack>
                </WhmListItem>
                <WhmListItem>
                  <WhmStack gap="8px" direction="row" alignItems="center">
                    <WhmBox flexShrink={0}>
                      <OkCheckmark
                        height="14px"
                        width="14px"
                        fill={whmTheme.palette.success.main}
                      />
                    </WhmBox>
                    <WhmTypography variant="body2">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_SAMPLE_GOOD_UPLOAD_PASSPORT_CONTENTS
                        }
                      />
                    </WhmTypography>
                  </WhmStack>
                </WhmListItem>
              </WhmList>
            </WhmStack>

            <WhmStack flex="1">
              <WhmStack gap="16px" direction="row" justifyContent="center">
                <PassportBad2 />
                <PassportBad1 />
              </WhmStack>

              <WhmList>
                <WhmListSubheader>
                  <Translate
                    translationKey={
                      translationKeys.PROFILE_KEY_PARTIES_SAMPLE_BAD_UPLOAD
                    }
                  />
                </WhmListSubheader>
                <WhmListItem>
                  <WhmStack gap="8px" direction="row" alignItems="center">
                    <WhmBox flexShrink={0}>
                      <BadCheckmark
                        height="14px"
                        width="14px"
                        fill={whmTheme.palette.error.main}
                      />
                    </WhmBox>
                    <WhmTypography variant="body2">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_SAMPLE_BAD_UPLOAD_QUALITY
                        }
                      />
                    </WhmTypography>
                  </WhmStack>
                </WhmListItem>
                <WhmListItem>
                  <WhmStack gap="8px" direction="row" alignItems="center">
                    <WhmBox flexShrink={0}>
                      <BadCheckmark
                        height="14px"
                        width="14px"
                        fill={whmTheme.palette.error.main}
                      />
                    </WhmBox>
                    <WhmTypography variant="body2">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_SAMPLE_BAD_UPLOAD_CROPPED
                        }
                      />
                    </WhmTypography>
                  </WhmStack>
                </WhmListItem>
                <WhmListItem>
                  <WhmStack gap="8px" direction="row" alignItems="center">
                    <WhmBox flexShrink={0}>
                      <BadCheckmark
                        height="14px"
                        width="14px"
                        fill={whmTheme.palette.error.main}
                      />
                    </WhmBox>
                    <WhmTypography variant="body2">
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_KEY_PARTIES_SAMPLE_BAD_UPLOAD_COMBINED
                        }
                      />
                    </WhmTypography>
                  </WhmStack>
                </WhmListItem>
              </WhmList>
            </WhmStack>
          </WhmResponsiveStack>
          <WhmTypography variant="body2" color="text.secondary">
            <Translate
              translationKey={
                translationKeys.PROFILE_KEY_PARTIES_SAMPLE_PASSPORT_UPLOAD_FOOTER
              }
            />
          </WhmTypography>
        </WhmStack>
      </WhmDialogContent>
      <WhmDialogActions>
        <WhmButton onClick={onClose} variant="contained">
          <Translate
            translationKey={translationKeys.PROFILE_KEY_PARTIES_SAMPLE_CLOSE}
          />
        </WhmButton>
      </WhmDialogActions>
    </WhmDialog>
  );
}
